// Fixed geometry and unit specs for the ground map. 1 px = 1 m.

import { BASE_DAMAGE, type EnemyKind } from "../../shared/ground";
import type { GunKind, SensorKind } from "./graph";

export const WIDTH = 960;
export const HEIGHT = 540;

export interface Point {
  x: number;
  y: number;
}

export type RoadId = "north" | "south" | "trail";

export const BASE: Point = { x: 915, y: 270 };
export { BASE_HP } from "../../shared/ground";

export const ROADS: Record<RoadId, Point[]> = {
  north: [
    { x: -30, y: 92 },
    { x: 160, y: 92 },
    { x: 290, y: 165 },
    { x: 470, y: 140 },
    { x: 620, y: 205 },
    { x: 770, y: 238 },
    BASE,
  ],
  south: [
    { x: -30, y: 462 },
    { x: 170, y: 455 },
    { x: 300, y: 375 },
    { x: 460, y: 405 },
    { x: 620, y: 338 },
    { x: 770, y: 302 },
    BASE,
  ],
  trail: [
    { x: -30, y: 282 },
    { x: 140, y: 258 },
    { x: 320, y: 292 },
    { x: 500, y: 262 },
    { x: 690, y: 276 },
    BASE,
  ],
};

export interface WeaponSpec {
  kind: GunKind;
  label: string;
  cost: number;
  range: number;
  minRange: number;
  reload: number;
  /** Shell flight time (s) for artillery, which fires high arcs so it is roughly constant; 0 for missiles. */
  flight: number;
  blast: number;
  damage: number;
  /** Damage multiplier against armour. */
  armorFactor: number;
  blurb: string;
}

export const WEAPONS: Record<GunKind, WeaponSpec> = {
  mortar: {
    kind: "mortar",
    label: "Mortar",
    cost: 120,
    range: 250,
    minRange: 40,
    reload: 1.4,
    flight: 1.3,
    blast: 30,
    damage: 2,
    armorFactor: 0.25,
    blurb: "Range 250 m, reloads in 1.4 s, shells land 1.3 s after firing. Small blast; barely scratches tanks.",
  },
  howitzer: {
    kind: "howitzer",
    label: "Howitzer",
    cost: 260,
    range: 640,
    minRange: 70,
    reload: 3.0,
    flight: 2.2,
    blast: 46,
    damage: 6,
    armorFactor: 1,
    blurb: "Range 640 m, reloads in 3 s, shells land 2.2 s after firing. Large blast that kills tanks.",
  },
  attackDrone: {
    kind: "attackDrone",
    label: "Attack drone",
    cost: 180,
    range: 220,
    minRange: 0,
    reload: 3.5,
    flight: 0,
    blast: 14,
    damage: 8,
    armorFactor: 1,
    blurb:
      "Orbits where you place it and fires a guided missile at targets within 220 m. Locks on if the report is within 30 m of a real enemy, so it needs no Lead Predictor. Kills tanks.",
  },
};

/** Attack drone missiles fly at this speed (m/s). */
export const MISSILE_SPEED = 260;
/** A missile locks on to the enemy nearest its aim point if one is within this distance. */
export const SEEKER_RADIUS = 30;
export const ATTACK_DRONE_ORBIT = 32;
export const ATTACK_DRONE_PERIOD_S = 6;

export interface FixedGun {
  id: string;
  kind: GunKind;
  label: string;
  pos: Point;
}

/** Guns the base starts with. They can't be moved or sold. */
export const FIXED_GUNS: FixedGun[] = [
  { id: "gun-mortarN", kind: "mortar", label: "Mortar N", pos: { x: 470, y: 50 } },
  { id: "gun-mortarS", kind: "mortar", label: "Mortar S", pos: { x: 470, y: 492 } },
  { id: "gun-howitzer", kind: "howitzer", label: "Howitzer", pos: { x: 850, y: 150 } },
];

export interface SensorSpec {
  kind: SensorKind;
  label: string;
  cost: number;
  range: number;
  /** Seconds between reports on the same target. */
  interval: number;
  /** Position error radius of each report. */
  error: number;
  classifies: boolean;
  measuresVelocity: boolean;
  blurb: string;
}

export const SENSORS: Record<SensorKind, SensorSpec> = {
  acoustic: {
    kind: "acoustic",
    label: "Acoustic",
    cost: 40,
    range: 220,
    interval: 1.0,
    error: 32,
    classifies: false,
    measuresVelocity: false,
    blurb: "Cheap and wide. Hears vehicles out to 220 m but infantry only within 90 m. Fixes are ±32 m with no class or velocity.",
  },
  camera: {
    kind: "camera",
    label: "Camera",
    cost: 80,
    range: 260,
    interval: 0.4,
    error: 3,
    classifies: true,
    measuresVelocity: true,
    blurb: "Precise (±3 m), classifies, measures velocity. Sees a 60° cone; click a placed camera to turn it.",
  },
  drone: {
    kind: "drone",
    label: "Drone",
    cost: 140,
    range: 130,
    interval: 0.5,
    error: 8,
    classifies: true,
    measuresVelocity: true,
    blurb: "Orbits where you place it and sees all round (130 m, ±8 m). Classifies and measures velocity.",
  },
};

export const ACOUSTIC_INFANTRY_RANGE = 90;
export const CAMERA_FOV = (60 * Math.PI) / 180;
export const DRONE_ORBIT = 45;
export const DRONE_PERIOD_S = 7;

export interface EnemySpec {
  hp: number;
  speed: number;
  radius: number;
  baseDamage: number;
  /** Credits earned for a kill, to spend on sensors and weapons. */
  reward: number;
}

export const ENEMIES: Record<EnemyKind, EnemySpec> = {
  infantry: { hp: 2, speed: 20, radius: 7, baseDamage: BASE_DAMAGE.infantry, reward: 10 },
  vehicle: { hp: 4, speed: 30, radius: 9, baseDamage: BASE_DAMAGE.vehicle, reward: 20 },
  armor: { hp: 14, speed: 24, radius: 12, baseDamage: BASE_DAMAGE.armor, reward: 50 },
};

export const START_CREDITS = 220;
export const WAVE_BREAK_S = 10;
export const ORDER_STALE_S = 1.5;
export const SELL_REFUND = 0.5;

export function distToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const k = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + dx * k), p.y - (a.y + dy * k));
}

export function distToRoad(p: Point, road: Point[]): number {
  let d = Infinity;
  for (let i = 1; i < road.length; i++) d = Math.min(d, distToSegment(p, road[i - 1]!, road[i]!));
  return d;
}
